import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { createElement } from "react";
import type { UserRole, AuthMeResponse } from "@shared/types";
import { onUnauthorized } from "../lib/authEvents";

export interface AuthUser {
  userId: number;
  role: UserRole;
  name: string;
  email: string;
  photoUrl: string | null;
  /** Server-resolved effective permissions (from /api/auth/me). Drives UI gating
   *  without re-implementing RBAC on the client. */
  permissions: {
    view_finance: boolean;
    view_members: boolean;
    view_applicants: boolean;
  };
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      try {
        const res = await fetch("/api/auth/me", { credentials: "same-origin" });
        if (!res.ok) {
          if (!cancelled) setUser(null);
          return;
        }
        const data = (await res.json()) as AuthMeResponse;
        if (!cancelled && data.role) {
          setUser({
            userId: data.userId,
            role: data.role,
            name: data.name,
            email: data.email,
            photoUrl: data.photoUrl,
            // Tolerate older payloads that predate the permissions field.
            permissions: {
              view_finance: data.permissions?.view_finance ?? false,
              view_members: data.permissions?.view_members ?? false,
              view_applicants: data.permissions?.view_applicants ?? false,
            },
          });
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  // Any hook that sees a 401 mid-session (session destroyed by the C-9
  // staleness gate, or naturally expired) reports it here via apiFetch.ts.
  // Clearing user forces AuthGate to render Login instead of leaving the
  // shell rendering as logged-in while individual widgets silently 401.
  useEffect(() => {
    return onUnauthorized(() => setUser(null));
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "same-origin",
      });
    } catch {
      // best-effort
    }
    setUser(null);
  }, []);

  const value: AuthContextValue = {
    isAuthenticated: user !== null,
    isLoading,
    user,
    logout,
  };

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
